import _ from 'lodash';
import { getPaymentItemFallbackKeys, getProfessionalServiceLineFallbackKeys } from './matching-keys';

export interface ProfessionalMatchingPaymentItem {
    controlNumber?: string;
    procedureCode?: string;
    modifierCodes?: string[];
    fromDos?: string;
    paymentId?: string;
}

export interface ProfessionalMatchingServiceLine {
    x12ControlNumber?: string;
    f24dProcedureCode?: string;
    f24dProcedureModifiers?: string[];
    f24aFromDateOfService?: string;
}

export function createProfessionalPaymentItemMatcher<TItem extends ProfessionalMatchingPaymentItem>(
    paymentItems: TItem[],
    existingControlNumbers: (string | undefined)[]
) {
    const existingControlNumbersUpper = new Set(
        existingControlNumbers.map(value => (value ? _.toUpper(_.trim(value)) : undefined)).filter(Boolean) as string[]
    );

    const itemsByControlKey = new Map<string, TItem[]>();
    const itemsByFallbackKey = new Map<string, TItem[]>();
    const paymentFallbackKeysByItem = new Map<TItem, Set<string>>();
    const usedPaymentItems = new Set<TItem>();

    paymentItems.forEach(item => {
        const controlKey = item.controlNumber ? _.toUpper(_.trim(item.controlNumber)) : undefined;
        const fallbackKeys = getPaymentItemFallbackKeys(item);
        paymentFallbackKeysByItem.set(item, new Set(fallbackKeys));

        if (controlKey && existingControlNumbersUpper.has(controlKey)) {
            const items = itemsByControlKey.get(controlKey) || [];
            items.push(item);
            itemsByControlKey.set(controlKey, items);
        }

        fallbackKeys.forEach(fallbackKey => {
            const items = itemsByFallbackKey.get(fallbackKey) || [];
            items.push(item);
            itemsByFallbackKey.set(fallbackKey, items);
        });
    });

    const pickUnusedOnePerPaymentId = (candidates: TItem[], allowedFallbackKeys?: Set<string>): TItem[] => {
        const chosen: TItem[] = [];
        const usedPaymentIds = new Set<string>();

        for (const item of candidates) {
            if (usedPaymentItems.has(item)) {
                continue;
            }

            if (allowedFallbackKeys && allowedFallbackKeys.size > 0) {
                const itemKeys = paymentFallbackKeysByItem.get(item);
                const matchesAllowed = itemKeys ? [...allowedFallbackKeys].some(key => itemKeys.has(key)) : false;
                if (!matchesAllowed) {
                    continue;
                }
            }

            const paymentId = item.paymentId;
            if (paymentId) {
                if (usedPaymentIds.has(paymentId)) {
                    continue;
                }

                usedPaymentIds.add(paymentId);
            }

            chosen.push(item);
        }

        return chosen;
    };

    const matchLine = (line: ProfessionalMatchingServiceLine): TItem[] => {
        const trimmedControlNumber = line.x12ControlNumber ? _.trim(line.x12ControlNumber) : '';
        const lineControlKey = trimmedControlNumber ? _.toUpper(trimmedControlNumber) : undefined;
        const lineFallbackKeys = getProfessionalServiceLineFallbackKeys(line);
        const allowedFallbackKeys = new Set(lineFallbackKeys);

        const fallbackCandidates: TItem[] = [];
        for (const key of lineFallbackKeys) {
            const candidates = itemsByFallbackKey.get(key) || [];
            fallbackCandidates.push(...candidates);
        }

        let foundPaymentItems: TItem[] = [];
        if (lineControlKey) {
            const controlCandidates = itemsByControlKey.get(lineControlKey) || [];
            foundPaymentItems = pickUnusedOnePerPaymentId(controlCandidates, allowedFallbackKeys);

            if (!foundPaymentItems.length) {
                foundPaymentItems = pickUnusedOnePerPaymentId(fallbackCandidates, allowedFallbackKeys);
            }
        } else if (fallbackCandidates.length) {
            foundPaymentItems = pickUnusedOnePerPaymentId(fallbackCandidates, allowedFallbackKeys);
        }

        foundPaymentItems.forEach(item => usedPaymentItems.add(item));
        return foundPaymentItems;
    };

    return {
        matchLine,
        getUsedPaymentItems: () => usedPaymentItems,
    };
}
